import type { AxiosError } from 'axios';
import { createQuery } from 'react-query-kit';

import type { Trim } from '@/api/cars/types';
import { client } from '@/api/common';
import type { RentalLocation } from '@/api/reservations';

export type VinTrimEntry = {
  trims: Trim[];
  locked: boolean;
  unrestricted?: boolean;
};

type Variables = {
  vins: string[];
};
type Response = {
  pickupLocations: RentalLocation[];
  trims: Trim[];
  vinTrims?: Record<string, VinTrimEntry | null>;
  licensePlateStates: {
    [name: string]: number;
  };
  autopilotTypes: {
    [name: string]: number;
  };
  colors: {
    [name: string]: number;
  };
  deliveryDistanceCostOptions: {
    [distance: string]: number;
  };
};

export const useCarDetailOptions = createQuery<Response, Variables, AxiosError>(
  {
    queryKey: ['car-detail-options'],
    fetcher: (variables) => {
      return client
        .get(`v2/internal/partners/cars/car-detail-options`, {
          params: {
            vins: variables.vins,
          },
          paramsSerializer: (params) => {
            return params.vins
              .map((vin: string) => `vins[]=${encodeURIComponent(vin)}`)
              .join('&');
          },
        })
        .then((response) => {
          return response.data.data;
        });
    },
  }
);

export const NO_VIN_TRIM_MESSAGE = 'No trim is available for this VIN.';

export function trimsForVin({
  vin,
  trims = [],
  vinTrims,
  modelId,
}: {
  vin: string;
  trims?: Trim[];
  vinTrims?: Record<string, VinTrimEntry | null | undefined>;
  modelId?: number;
}): { trims: Trim[]; locked: boolean } {
  const entry = vinTrims?.[vin];

  if (!entry || entry.unrestricted) {
    return {
      trims: trims.filter((trim) =>
        modelId == null ? true : trim.modelId === modelId
      ),
      locked: false,
    };
  }

  return {
    trims: entry.trims ?? [],
    locked: Boolean(entry.locked) && (entry.trims?.length ?? 0) > 0,
  };
}

export function selectedTrimId({
  options,
  locked,
  currentTrimId,
}: {
  options: { id: string }[];
  locked: boolean;
  currentTrimId?: string;
}): string {
  if (locked) {
    return options[0]?.id ?? '';
  }

  if (
    currentTrimId &&
    options.some((option) => option.id === currentTrimId)
  ) {
    return currentTrimId;
  }

  return '';
}
